import {
  COUPON_STATUS_TEXT,
  COUPON_TYPE,
  COUPON_VALUE_TYPE,
} from "../../services/utils";
import { formatters } from "../../utils/formatters";
import { validPlaceTypes } from "./DynamicCompanyForm";
import * as texts from "./locales";
import {
  buildOcpiStationKey,
  decodeOcpiStationId,
  stationMatchesOcpiIdentity,
} from "./ocpiStationIdentity";

const { FREE, PERCENTAGE, FIXED_VALUE, CREDIT, STATION_FIXED_VALUE } =
  COUPON_TYPE;

/**
 * Separa as estações escolhidas entre rede própria (`stationIds`, por id do
 * documento) e roaming (`ocpiStations`, por identidade do parceiro). Uma estação
 * de roaming não pode ser referenciada pelo id do documento espelhado, porque
 * esse documento é compartilhado por todas as parties que publicam o mesmo EVSE.
 */
const splitStationsByOrigin = (stationIds, { allStations, partyId }) => {
  const ownNetwork = [];
  const ocpiStations = [];

  (stationIds ?? []).forEach((stationId) => {
    const station = (allStations ?? []).find(({ _id }) => _id === stationId);
    const decoded = decodeOcpiStationId(station?.stationID);

    if (!decoded) {
      ownNetwork.push(stationId);
      return;
    }

    // A party da própria estação (vinda do OCPI) manda; a da empresa é fallback
    // para catálogos que ainda não a informem. Sem nenhuma das duas, a estação
    // fica de fora, em vez de ser gravada de um jeito que nunca casa.
    const stationPartyId = station?.ocpiPartyId ?? partyId;
    if (!stationPartyId) return;

    ocpiStations.push({
      partyId: stationPartyId,
      locationId: decoded.locationId,
      evseUid: decoded.evseUid,
    });
  });

  return { stationIds: ownNetwork, ocpiStations };
};

/**
 * Identidades de roaming da campanha que NÃO existem no catálogo desta tela.
 *
 * A seleção de roaming é reconstruída por lookup em `allStations`, e lookup que
 * não acha nada vira lista vazia — que o backend lê como "vale em toda a
 * empresa". Como o catálogo vem do espelho, a estação da outra party pode
 * simplesmente não estar nele, e aí abrir e salvar a campanha apagaria a
 * restrição em silêncio: exatamente o estrago que este formato veio evitar.
 *
 * Só olha a campanha original, nunca a seleção atual: uma identidade que existe
 * no catálogo e foi desmarcada é decisão do usuário, não perda.
 */
export const findUnresolvedOcpiStations = (
  campaignToEdit,
  allStations = [],
  allCompanies = []
) =>
  (campaignToEdit?.availableCompanies ?? []).flatMap((availableCompany) => {
    const fallbackPartyId = allCompanies.find(
      ({ _id }) => _id === availableCompany.companyId
    )?.ocpiPartyId;

    return (availableCompany.ocpiStations ?? [])
      .filter(
        (ocpiStation) =>
          !(allStations ?? []).some((station) =>
            stationMatchesOcpiIdentity(station, ocpiStation, fallbackPartyId)
          )
      )
      .map((ocpiStation) => ({
        companyId: availableCompany.companyId,
        ...ocpiStation,
      }));
  });

/**
 * Devolve as identidades de roaming preservadas para uma empresa, sem repetir
 * as que a seleção atual já produziu.
 */
const keepUnresolvedOcpiStations = (
  ocpiStations,
  preservedOcpiStations,
  companyId
) => {
  const alreadyThere = new Set(ocpiStations.map(buildOcpiStationKey));

  return preservedOcpiStations
    .filter((preserved) => preserved.companyId === companyId)
    .map(({ partyId, locationId, evseUid }) => ({
      partyId,
      locationId,
      evseUid,
    }))
    .filter((preserved) => !alreadyThere.has(buildOcpiStationKey(preserved)));
};

/**
 * `preservedOcpiStations` são as identidades que esta tela não consegue
 * resolver no catálogo (ver `findUnresolvedOcpiStations`). Elas voltam para o
 * payload como estavam: a tela não sabe exibi-las, e omiti-las transformaria a
 * restrição em "vale em toda a empresa" sem ninguém pedir. Só valem para
 * `SPECIFIC_STATIONS`; trocar a campanha para `ALL_COMPANIES` é uma decisão
 * explícita de tirar toda restrição, e aí nada é preservado.
 */
export const formatAvailableCompanies = (
  params,
  userCompany,
  { allStations = [], allCompanies = [], preservedOcpiStations = [] } = {}
) => {
  switch (params.validPlace) {
    case validPlaceTypes.ALL_COMPANIES: {
      return [
        {
          companyId: params?.company ?? userCompany.id,
          stationIds: [],
          // Explícito: o backend mantém a restrição de roaming de uma entrada
          // que OMITE `ocpiStations` (é como ele protege campanha migrada de
          // cliente antigo). Aqui a intenção é justamente tirar toda
          // restrição, então o array vazio precisa ser dito.
          ocpiStations: [],
        },
      ];
    }
    case validPlaceTypes.SPECIFIC_STATIONS: {
      const companies = params.companies.filter(
        (company) => company !== undefined
      );

      const someInvalidInput = companies.some(
        (company) =>
          company.specificStations && (company.stationIds ?? []).length === 0
      );

      if (someInvalidInput) {
        return false;
      }

      params.companies = undefined;

      return companies.map((company) => {
        const partyId = allCompanies.find(
          ({ _id }) => _id === company.companyId
        )?.ocpiPartyId;

        const { stationIds, ocpiStations } = company.specificStations
          ? splitStationsByOrigin(company.stationIds, { allStations, partyId })
          : { stationIds: [], ocpiStations: [] };

        return {
          companyId: company.companyId,
          stationIds,
          ocpiStations: [
            ...ocpiStations,
            ...keepUnresolvedOcpiStations(
              ocpiStations,
              preservedOcpiStations,
              company.companyId
            ),
          ],
        };
      });
    }
    default: {
      return [];
    }
  }
};

export const getBenefitDescription = (benefit, currency, freire) => {
  switch (benefit.type) {
    case FREE:
      return {
        iconColor: "#48ac16",
        tagColor: "green",
        text: freire(texts.FREE),
      };
    case PERCENTAGE:
      return {
        iconColor: "#1890ff",
        tagColor: "blue",
        text: freire(
          texts.DISCOUNT(
            formatters.percentage(freire.userLanguage)(benefit.value)
          )
        ),
      };
    case FIXED_VALUE:
      return {
        iconColor: "#722ed1",
        tagColor: "purple",
        text: freire(
          texts.DISCOUNT(
            formatters.currency(
              freire.userLanguage,
              currency
            )(benefit.value / 100)
          )
        ),
      };
    case CREDIT:
      if (benefit.valueType === COUPON_VALUE_TYPE.KWH) {
        return {
          iconColor: "#eb7c05",
          tagColor: "orange",
          text: freire(texts.CREDIT_KWH(benefit.value)),
        };
      }
      return {
        iconColor: "#eb7c05",
        tagColor: "orange",
        text: freire(
          texts.CREDIT_CURRENCY(
            formatters.currency(
              freire.userLanguage,
              currency
            )(benefit.value / 100)
          )
        ),
      };
    case STATION_FIXED_VALUE:
      return {
        iconColor: "#c44925",
        tagColor: "orange",
        text: freire(texts.STATION_FIXED_VALUE_TAG(benefit.value)),
      };
    case "FLEET":
      return {
        tagColor: "brown",
        text: freire(texts.FLEET),
      };

    default:
      return {};
  }
};

export const getCouponStatusText = (status) => COUPON_STATUS_TEXT[status];

export const formatDuration = (formField) => {
  if (formField?.enabled) {
    const { unit, value } = formField;
    return `P${value}${unit}`;
  }

  return undefined;
};

// Inverse of formatDuration: splits an ISO-8601 duration like "P10D" back
// into { value: 10, unit: "D" }. A fixed-position charAt would break for
// two-digit values (e.g. "P10D" -> unit "0" instead of "D").
export const parseDuration = (period) => {
  const match = period?.match(/^P(\d+)([DWMY])$/);

  return {
    value: match ? Number(match[1]) : undefined,
    unit: match ? match[2] : undefined,
  };
};

export const formatUsageLimitPerPeriod = (usageLimitPerPeriod) => {
  if (usageLimitPerPeriod.enabled) {
    return {
      usageAmount: Number(usageLimitPerPeriod.usageAmount),
      period: formatDuration(usageLimitPerPeriod),
    };
  }

  return undefined;
};

export const wordsToExcludeFromCouponSheet = ["chassi"];
